import { API_BASE } from './api-base';

const DUPLEXER_HOST = 'duplexer.wix.com';
const APP_DEF_ID = 'f5b4ce00-4f7b-4066-a607-0277d16780b8';

export type DuplexerEventName = 'created' | 'updated' | 'deleted' | 'action';

export interface DuplexerPayload<T> {
  entityId?: string;
  entity?: T | null;
}

type Listener = (payload: DuplexerPayload<unknown>) => void;

/**
 * ONE duplexer connection for the whole app, not one per subscription.
 *
 * Every channel used to open its OWN `new Duplexer().connect()` — a separate WebSocket AND a separate
 * `/api/auth/duplexer-instance` mint — for every mounted component that called a `use*Duplexer` hook. With
 * the home alone subscribing dashboard / file-system / assistant-chat (and each screen adding agent-task,
 * goal, workspace, knowledge…), a single view held a fistful of live sockets. This module mints the instance
 * once, opens ONE Duplexer + ONE VirtualSocket lazily on first use, and MULTIPLEXES every channel + listener
 * over it: a channel is `socket.subscribe`d once and fans its events out to all registered callbacks.
 *
 * HTTPS-only, same as before — duplexer-js picks ws:// on plain http and the browser blocks the upgrade
 * (local dev runs --experimental-https, so it works there too). The connection is kept alive for the page
 * lifetime; unsubscribing only drops a listener, never the shared socket, so navigation doesn't churn it.
 */

// Minted lazily and refreshed on demand: duplexer-js calls getInstance on connect AND on token refresh, so
// re-fetching here (rather than pinning one token forever, as the per-hook version did) keeps a long-lived
// connection able to re-authenticate.
async function fetchInstance(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/auth/duplexer-instance`, { credentials: 'include' });
  if (!res.ok) throw new Error(`duplexer-instance ${res.status}`);
  const { instance } = (await res.json()) as { instance?: string };
  if (!instance) throw new Error('duplexer-instance missing');
  return instance;
}

interface ChannelRegistration {
  channel: { on(event: string, handler: (payload: DuplexerPayload<unknown>) => void): void };
  listeners: Map<DuplexerEventName, Set<Listener>>;
  bound: Set<DuplexerEventName>;
}

interface Socket {
  subscribe(channelName: string): ChannelRegistration['channel'];
}

let socketPromise: Promise<Socket> | null = null;
const channels = new Map<string, ChannelRegistration>();

async function ensureSocket(): Promise<Socket> {
  if (!socketPromise) {
    socketPromise = (async () => {
      const { Duplexer } = await import('@wix/duplexer-js');
      const duplexer = new Duplexer(DUPLEXER_HOST, { instanceUpdater: { getInstance: fetchInstance } });
      return duplexer.connect({ appDefId: APP_DEF_ID }) as unknown as Socket;
    })().catch((err) => {
      // Let a later subscription retry the connect instead of caching the rejection forever.
      socketPromise = null;
      throw err;
    });
  }
  return socketPromise;
}

/**
 * Register a listener on a channel over the shared connection. Returns a synchronous disposer that removes
 * just this listener (the shared socket and the channel subscription stay up). Safe to dispose before the
 * async connect resolves.
 */
export function subscribeToChannel(
  channelName: string,
  events: DuplexerEventName[],
  listener: Listener,
): () => void {
  let disposed = false;
  let removeListener: (() => void) | null = null;

  void ensureSocket()
    .then((socket) => {
      if (disposed) return;
      let reg = channels.get(channelName);
      if (!reg) {
        reg = { channel: socket.subscribe(channelName), listeners: new Map(), bound: new Set() };
        channels.set(channelName, reg);
      }
      const registration = reg;
      for (const event of events) {
        if (!registration.bound.has(event)) {
          registration.bound.add(event);
          registration.channel.on(event, (payload) => registration.listeners.get(event)?.forEach((fn) => fn(payload)));
        }
        let set = registration.listeners.get(event);
        if (!set) {
          set = new Set();
          registration.listeners.set(event, set);
        }
        set.add(listener);
      }
      removeListener = () => {
        for (const event of events) registration.listeners.get(event)?.delete(listener);
      };
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn(`[orion-web/duplexer:${channelName}] setup failed`, err);
    });

  return () => {
    disposed = true;
    removeListener?.();
  };
}

/**
 * How many distinct listeners this channel would fan a single event out to.
 *
 * One event runs `listeners.forEach(fn => fn(payload))`, so this number IS the multiplier on
 * whatever each listener does — and every `useTasks` instance registers one that refetches the
 * same cache entry. Read by the runaway-fetch probe so a burst report carries its own fan-out
 * factor instead of leaving it to be inferred from a stack.
 */
export function channelListenerCount(channelName: string): number {
  const registration = channels.get(channelName);
  if (!registration) return 0;
  const distinct = new Set<Listener>();
  registration.listeners.forEach((set) => set.forEach((listener) => distinct.add(listener)));
  return distinct.size;
}
